import { ref, Ref } from 'vue';
import { defineStore } from 'pinia';

import apiGw from '@/services/apiGw';
import { SseListener } from '@/utils/general';
import { getUidsFromUri } from '@/utils/links';
import { useEventStreamStore } from '@/store/eventStreamStore';
import { createResourceLoadingMixin } from '@/store/resourceLoadingMixin';
import { PendingPaymentOrderInfo } from '@/components/payments/types/payment';

const resourceLoadingMixin = createResourceLoadingMixin();

export const usePaymentOrdersStore = defineStore('paymentOrders', () => {
  const eventStreamStore = useEventStreamStore();

  // STATES
  const paymentOrders: Ref<{ [key: string]: PendingPaymentOrderInfo }> = ref({});
  // GETTERS

  // ACTIONS
  function $reset() {
    paymentOrders.value = {};
  }
  /**
   * Removes the given payment order from the store (can be used to trigger a force reload later on when needed)
   * @param paymentOrderUid
   */
  function removePaymentOrder(paymentOrderUid: string) {
    delete paymentOrders.value[paymentOrderUid];
  }

  async function loadPaymentOrders(uri: string, forceReload = false): Promise<PendingPaymentOrderInfo[]> {
    if (forceReload) {
      resourceLoadingMixin.removeResource(`loadPaymentOrders-${uri}`);
    }
    return resourceLoadingMixin.loadResource(
      `loadPaymentOrders-${uri}`,
      async () => {
        const response = await apiGw.get(uri);
        return response.data;
      },
      forceReload
    );
  }

  async function getPaymentOrder(
    paymentOrderUri: string,
    forceReload?: boolean
  ): Promise<PendingPaymentOrderInfo | undefined> {
    const paymentOrderUid = getUidsFromUri(paymentOrderUri)[0];
    const po = paymentOrders.value[paymentOrderUid];
    const isPoStale =
      !!eventStreamStore.stale_payment_orders && !!eventStreamStore.stale_payment_orders[paymentOrderUid];

    // If the payment order is already in the store and we don't want to force a reload, AND it is not marked as stale return it
    if (po && !forceReload && !isPoStale) {
      return po;
    }
    const response = await apiGw.get(paymentOrderUri);
    unstalePaymentOrderIfOlder(paymentOrderUid, new Date().getTime());

    paymentOrders.value[paymentOrderUid] = response.data;
    return paymentOrders.value[paymentOrderUid];
  }

  function unstalePaymentOrderIfOlder(uid: string, timeOfResponse: number) {
    if (!eventStreamStore.stale_payment_orders) {
      return;
    }
    const staleTime = eventStreamStore.stale_payment_orders[uid]?.time;
    if (staleTime < timeOfResponse) {
      eventStreamStore.unstaleUid(uid, SseListener.PaymentOrder);
    }
  }
  return {
    // STATES
    paymentOrders,
    // GETTERS

    // ACTIONS
    $reset,
    loadPaymentOrders,
    removePaymentOrder,
    getPaymentOrder
  };
});
